import React from 'react';

/**
 * ErrorBoundary para envolver lazy-loaded tabs.
 *
 * Captura particularmente erros do tipo `ChunkLoadError` que ocorrem após
 * deploys em produção (chunks JS antigos no cache do browser que já não
 * existem no servidor). Mostra UI amigável com botão de recarregar em vez
 * de deixar o `<main>` ficar branco.
 */
class TabErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    const msg = String(error?.message || error || '');
    const name = String(error?.name || '');
    const isChunkError =
      name === 'ChunkLoadError' ||
      /Loading chunk|Failed to fetch dynamically imported|Loading CSS chunk/i.test(msg);
    return { error, isChunkError };
  }

  componentDidCatch(error, info) {
    // Log para a consola — útil em produção para inspecção via DevTools
    // eslint-disable-next-line no-console
    console.error('[TabErrorBoundary]', error, info);
  }

  handleReload = () => {
    // Em ChunkLoadError, fazer hard reload para recarregar o index.html novo
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({ error: null, isChunkError: false });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const { isChunkError } = this.state;

    return (
      <div
        className="flex flex-col items-center justify-center min-h-[40vh] p-6 text-center"
        data-testid="tab-error-boundary"
      >
        <div className="max-w-md space-y-4">
          <div className="text-5xl">{isChunkError ? '🔄' : '⚠️'}</div>
          <h2 className="text-xl font-semibold">
            {isChunkError ? 'Nova versão disponível' : 'Erro ao carregar este painel'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isChunkError
              ? 'A aplicação foi actualizada entretanto. Por favor recarregue a página para obter a versão mais recente.'
              : 'Algo correu mal ao carregar este conteúdo. Pode tentar novamente ou recarregar a página.'}
          </p>
          {!isChunkError && this.state.error && (
            <details className="text-xs text-left bg-muted rounded p-2 max-h-48 overflow-auto" open>
              <summary className="cursor-pointer text-muted-foreground font-medium">Detalhes técnicos (partilhe ao suporte)</summary>
              <pre className="whitespace-pre-wrap break-words mt-2 text-[11px] leading-tight">
                <strong>{String(this.state.error?.name || 'Error')}: {String(this.state.error?.message || this.state.error)}</strong>
                {this.state.error?.stack ? '\n\n' + String(this.state.error.stack).split('\n').slice(0, 8).join('\n') : ''}
              </pre>
            </details>
          )}
          <div className="flex gap-2 justify-center">
            {!isChunkError && (
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 rounded-md border border-input bg-background hover:bg-accent text-sm"
                data-testid="tab-error-retry"
              >
                Tentar novamente
              </button>
            )}
            <button
              onClick={this.handleReload}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 text-sm"
              data-testid="tab-error-reload"
            >
              Recarregar página
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default TabErrorBoundary;
